/**
 * BỘ VẼ NHÂN VẬT (paper-doll) — vẽ từng mảnh bằng Canvas2D rồi nạp vào Phaser.
 *
 * Vì sao Canvas2D chứ không phải Phaser.Graphics:
 *   - có gradient, viền nét mượt, đổ bóng ⇒ nhìn "đẹp" hơn hẳn hình khối
 *   - mỗi mảnh được CACHE theo khoá (hình + màu) ⇒ 300 unit vẫn nhẹ
 *   - đổi phụ kiện = đổi 1 texture, KHÔNG dựng lại nhân vật
 *
 * Sau này thay bằng ảnh AI: chỉ cần đổi hàm trong file này thành load ảnh.
 */
import Phaser from 'phaser';

export type ShapeKind =
  | 'head'
  | 'torso'
  | 'limb'
  | 'foot'
  | 'hair'
  | 'hat'
  | 'glasses'
  | 'weapon'
  | 'joint'
  | 'aura';

export interface PartStyle {
  shape: ShapeKind;
  variant: string;
  color: number;
  accent: number;
  skin: number;
  w: number;
  h: number;
}

const OUTLINE = '#2a1f1a';
const PAD = 5;

// ------------------------------------------------------------------ tiện ích

function hex(c: number): string {
  return `#${(c & 0xffffff).toString(16).padStart(6, '0')}`;
}

function shade(c: number, amt: number): string {
  const r = Math.max(0, Math.min(255, ((c >> 16) & 255) + amt));
  const g = Math.max(0, Math.min(255, ((c >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (c & 255) + amt));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.lineTo(x + w - rad, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rad);
  ctx.lineTo(x + w, y + h - rad);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rad, y + h);
  ctx.lineTo(x + rad, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rad);
  ctx.lineTo(x, y + rad);
  ctx.quadraticCurveTo(x, y, x + rad, y);
  ctx.closePath();
}

function poly(ctx: CanvasRenderingContext2D, pts: Array<[number, number]>): void {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
}

function stroke(ctx: CanvasRenderingContext2D, width = 2.4): void {
  ctx.lineWidth = width;
  ctx.strokeStyle = OUTLINE;
  ctx.lineJoin = 'round';
  ctx.stroke();
}

// ------------------------------------------------------------------ bộ vẽ

/** Tên texture atlas dùng chung cho MỌI mảnh nhân vật. */
export const PARTS_ATLAS = '__tdk_parts';
/**
 * CỠ ATLAS.
 *
 * Lịch sử: 2048² đủ cho 5 class. Khi thêm QUÁI + PHỤ KIỆN bằng ảnh AI thì atlas đầy
 * ⇒ `addCanvasFrame` ném lỗi NGAY TRONG `preloadAll()` ⇒ `onBoot` dừng giữa đường
 * ⇒ banner "Vào trận" không bao giờ hiện (màn hình trắng, không báo gì).
 * 3072² chứa được toàn bộ mảnh AI (đã thu về 192px) + mảnh vector.
 */
const ATLAS_SIZE = 3072;

/**
 * CỠ TỐI ĐA CỦA 1 MẢNH AI khi nạp vào atlas (px, cạnh dài nhất).
 *
 * Ảnh agy vẽ mảnh cỡ ~300px, nhưng trên màn hình mảnh chỉ hiện ~20-60px.
 * Thu về 192px vẫn dư độ nét (gấp 3-4 lần cỡ hiển thị) mà tiết kiệm ~2,4 lần
 * diện tích atlas ⇒ nhét được cả quái lẫn phụ kiện vào cùng 1 atlas.
 */
export const MAX_AI_FRAME = 192;

/** Thu nhỏ canvas cho vừa `maxDim` (giữ tỉ lệ). Trả về chính canvas cũ nếu đã nhỏ. */
export function fitCanvas(src: HTMLCanvasElement, maxDim = MAX_AI_FRAME): HTMLCanvasElement {
  const longest = Math.max(src.width, src.height);
  if (longest <= maxDim) return src;
  const k = maxDim / longest;
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(src.width * k));
  out.height = Math.max(1, Math.round(src.height * k));
  const ctx = out.getContext('2d');
  if (!ctx) return src;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

export class PartFactory {
  private cache = new Map<string, string>();
  private counter = 0;
  private atlas!: Phaser.Textures.CanvasTexture;
  private atlasCanvas!: HTMLCanvasElement;
  private atlasCtx!: CanvasRenderingContext2D;
  private cx = 0;
  private cy = 0;
  private rowH = 0;
  private full = false;
  /**
   * ĐANG GOM LÔ.
   *
   * ⚠️ BẪY ĐÃ SẬP (đo được): `atlas.refresh()` đẩy LẠI TOÀN BỘ atlas lên GPU.
   * Ở 3072² là ~37 MB mỗi lần. Nạp ~120 mảnh AI mà refresh từng mảnh ⇒ 4,5 GB
   * upload ⇒ boot mất **39 giây** (đo bằng Chrome thật) và game như bị treo.
   * Cách sửa: gom lô, chỉ refresh MỘT lần sau khi nạp xong.
   */
  private batching = false;
  private dirty = false;
  /**
   * ĐỘ SÂU GOM LÔ.
   *
   * ⚠️ Vì sao ĐẾM thay vì cờ true/false: `preloadAll()` gom lô rồi `await` (nhường main thread)
   * — trong lúc đó người chơi có thể mở tab Trang phục và nạp 1 dáng mới (cũng gom lô). Nếu
   * `endBatch()` của dáng đặt thẳng `batching = false` thì lô của `preloadAll` bị PHÁ: atlas
   * refresh giữa đường và các mảnh sau đó refresh lẻ từng mảnh (~4,5 GB upload — đúng lỗi cũ).
   * Đếm độ sâu ⇒ chỉ lô NGOÀI CÙNG mới thật sự `refresh()`.
   */
  private batchDepth = 0;

  constructor(private scene: Phaser.Scene) {
    this.initAtlas();
  }

  /** Bắt đầu gom lô: tạm hoãn `refresh()` cho tới `endBatch()` ngoài cùng. */
  beginBatch(): void {
    this.batchDepth++;
    this.batching = true;
  }

  /** Kết thúc gom lô: chỉ lô NGOÀI CÙNG mới đẩy atlas lên GPU. */
  endBatch(): void {
    this.batchDepth = Math.max(0, this.batchDepth - 1);
    if (this.batchDepth > 0) return;
    this.batching = false;
    if (this.dirty) {
      this.atlas.refresh();
      this.dirty = false;
    }
  }

  private markDirty(): void {
    if (this.batching) this.dirty = true;
    else this.atlas.refresh();
  }

  /**
   * GOM TẤT CẢ MẢNH VÀO 1 ATLAS.
   * Đây là quyết định hiệu năng quan trọng nhất: 60 unit × 15 mảnh = 900 sprite
   * nhưng chỉ còn 1 texture ⇒ Phaser batch gần hết thành 1 draw call.
   */
  private initAtlas(): void {
    if (this.scene.textures.exists(PARTS_ATLAS)) {
      this.atlas = this.scene.textures.get(PARTS_ATLAS) as Phaser.Textures.CanvasTexture;
      this.atlasCanvas = this.atlas.getSourceImage() as HTMLCanvasElement;
    } else {
      this.atlasCanvas = document.createElement('canvas');
      this.atlasCanvas.width = ATLAS_SIZE;
      this.atlasCanvas.height = ATLAS_SIZE;
      const created = this.scene.textures.addCanvas(PARTS_ATLAS, this.atlasCanvas);
      if (!created) throw new Error('Không tạo được atlas texture');
      this.atlas = created;
    }
    const ctx = this.atlasCanvas.getContext('2d');
    if (!ctx) throw new Error('Không tạo được context cho atlas');
    this.atlasCtx = ctx;
  }

  /** Cấp phát 1 ô trong atlas (xếp theo hàng). */
  private alloc(w: number, h: number): { x: number; y: number } | null {
    if (this.full) return null;
    if (this.cx + w > ATLAS_SIZE) {
      this.cx = 0;
      this.cy += this.rowH + 2;
      this.rowH = 0;
    }
    if (this.cy + h > ATLAS_SIZE) {
      this.full = true;
      return null;
    }
    const pos = { x: this.cx, y: this.cy };
    this.cx += w + 2;
    this.rowH = Math.max(this.rowH, h);
    return pos;
  }

  /**
   * Nạp một canvas BÊN NGOÀI (ảnh AI đã tách nền) vào atlas chung.
   * Trả về kích thước thật để lớp dựng xương biết tỉ lệ; `null` nếu atlas đã đầy
   * (KHÔNG ném lỗi — thiếu 1 mảnh thì unit đó lùi về art vector, game vẫn chạy).
   */
  addCanvasFrame(key: string, canvas: HTMLCanvasElement): { key: string; w: number; h: number } | null {
    const w = canvas.width;
    const h = canvas.height;
    if (!this.atlas.has(key)) {
      const pos = this.alloc(w, h);
      if (!pos) {
        console.warn(`[art] atlas đầy (${ATLAS_SIZE}²) — bỏ mảnh AI "${key}", unit đó dùng art vector`);
        return null;
      }
      this.atlasCtx.drawImage(canvas, pos.x, pos.y);
      this.atlas.add(key, 0, pos.x, pos.y, w, h);
      this.markDirty();
      this.counter++;
    }
    this.cache.set(key, key);
    return { key, w, h };
  }

  key(spec: PartStyle): string {
    return `p${spec.shape}_${spec.variant}_${spec.color}_${spec.accent}_${spec.skin}_${spec.w}x${spec.h}`;
  }

  /** Trả về khoá FRAME trong atlas, sinh mới nếu chưa có. */
  get(spec: PartStyle): string {
    const k = this.key(spec);
    if (this.cache.has(k)) return k;
    if (this.atlas.has(k)) {
      this.cache.set(k, k);
      return k;
    }
    const w = Math.ceil(spec.w) + PAD * 2;
    const h = Math.ceil(spec.h) + PAD * 2;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Không tạo được canvas 2d');
    ctx.translate(PAD, PAD);

    switch (spec.shape) {
      case 'head': this.drawHead(ctx, spec); break;
      case 'torso': this.drawTorso(ctx, spec); break;
      case 'limb': this.drawLimb(ctx, spec); break;
      case 'foot': this.drawFoot(ctx, spec); break;
      case 'hair': this.drawHair(ctx, spec); break;
      case 'hat': this.drawHat(ctx, spec); break;
      case 'glasses': this.drawGlasses(ctx, spec); break;
      case 'weapon': this.drawWeapon(ctx, spec); break;
      case 'joint': this.drawJoint(ctx, spec); break;
      case 'aura': this.drawAura(ctx, spec); break;
    }

    // dán vào atlas + đăng ký frame
    const pos = this.alloc(w, h);
    if (!pos) throw new Error(`Atlas mảnh nhân vật đã đầy (${ATLAS_SIZE}²) — tăng ATLAS_SIZE`);
    this.atlasCtx.drawImage(canvas, pos.x, pos.y);
    this.atlas.add(k, 0, pos.x, pos.y, w, h);
    this.markDirty();
    this.cache.set(k, k);
    this.counter++;
    return k;
  }

  get textureCount(): number {
    return this.counter;
  }

  // ---------------------------------------------------------------- đầu (góc 3/4 nghiêng PHẢI)
  private drawHead(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const w = s.w;
    const h = s.h;
    const cx = w / 2;
    const cy = h / 2;

    // tai — nằm bên TRÁI (phía xa) vì mặt quay sang phải
    ctx.fillStyle = shade(s.skin, -22);
    ctx.beginPath();
    ctx.ellipse(cx - w * 0.33, cy + 1.5, 3.6, 5.2, 0, 0, Math.PI * 2);
    ctx.fill();
    stroke(ctx, 2);

    // khối mặt: hơi dẹt bên trái, đầy hơn bên phải (gò má hướng về phía nhìn)
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, shade(s.skin, 8));
    grad.addColorStop(0.65, shade(s.skin, 18));
    grad.addColorStop(1, shade(s.skin, -16));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(cx + w * 0.03, cy, w * 0.46, h * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();
    stroke(ctx);

    // mũi nhô ra ở mép phải
    ctx.fillStyle = shade(s.skin, -6);
    ctx.beginPath();
    ctx.moveTo(cx + w * 0.34, cy - 1.5);
    ctx.quadraticCurveTo(cx + w * 0.5, cy + 2.5, cx + w * 0.32, cy + 5.5);
    ctx.closePath();
    ctx.fill();
    stroke(ctx, 2);

    // mắt: mắt GẦN (phải) to hơn, mắt XA (trái) nhỏ hơn ⇒ đọc ra góc 3/4
    const eyeY = cy - 0.5;
    const eyeNearX = cx + w * 0.16;
    const eyeFarX = cx - w * 0.15;

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(eyeNearX, eyeY, 4.4, 4.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(eyeFarX, eyeY, 3.4, 4.0, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#1f2937';
    ctx.beginPath();
    ctx.ellipse(eyeNearX + 0.6, eyeY + 0.5, 2.4, 2.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(eyeFarX + 0.6, eyeY + 0.5, 1.9, 2.3, 0, 0, Math.PI * 2);
    ctx.fill();

    // lông mày "quyết tâm", nghiêng theo hướng nhìn
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(eyeNearX - 4.6, eyeY - 6.4);
    ctx.lineTo(eyeNearX + 4.2, eyeY - 4.6);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(eyeFarX - 3.6, eyeY - 5.8);
    ctx.lineTo(eyeFarX + 3.2, eyeY - 4.4);
    ctx.stroke();

    // miệng lệch về phía nhìn
    ctx.beginPath();
    ctx.moveTo(cx + w * 0.06, cy + 7.6);
    ctx.quadraticCurveTo(cx + w * 0.16, cy + 10.2, cx + w * 0.28, cy + 7.4);
    ctx.stroke();

    // má hồng bên má nhìn thấy
    ctx.fillStyle = 'rgba(244,114,182,0.32)';
    ctx.beginPath();
    ctx.ellipse(cx - w * 0.08, cy + 4.5, 3.2, 2.1, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // ---------------------------------------------------------------- thân
  private drawTorso(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const { w, h, variant } = s;
    const main = hex(s.color);
    const dark = shade(s.color, -34);
    const light = shade(s.color, 20);

    if (variant === 'robe') {
      // đạo bào: dài, có vạt và đai
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, light);
      g.addColorStop(1, dark);
      ctx.fillStyle = g;
      poly(ctx, [
        [w * 0.28, 0],
        [w * 0.72, 0],
        [w, h],
        [0, h],
      ]);
      ctx.fill();
      stroke(ctx);
      ctx.fillStyle = hex(s.accent);
      rr(ctx, -1, h * 0.52, w + 2, 5, 2);
      ctx.fill();
      stroke(ctx, 2);
      // cổ áo chéo (3/4)
      ctx.fillStyle = hex(s.skin);
      poly(ctx, [
        [w * 0.44, 0],
        [w * 0.74, 0],
        [w * 0.6, h * 0.24],
      ]);
      ctx.fill();
      stroke(ctx, 2);
    } else if (variant === 'tank') {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, light);
      g.addColorStop(1, dark);
      ctx.fillStyle = g;
      rr(ctx, w * 0.12, 0, w * 0.76, h, 5);
      ctx.fill();
      stroke(ctx);
      // vai trần + dây
      ctx.fillStyle = hex(s.skin);
      ctx.beginPath();
      ctx.ellipse(w * 0.16, 3, 4, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(w * 0.84, 3, 4, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = hex(s.skin);
      ctx.beginPath();
      ctx.ellipse(w * 0.5, 2, 6, 4, 0, 0, Math.PI * 2);
      ctx.fill();
    } else if (variant === 'suit') {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, light);
      g.addColorStop(1, dark);
      ctx.fillStyle = g;
      rr(ctx, w * 0.1, 0, w * 0.8, h, 4);
      ctx.fill();
      stroke(ctx);
      // ve áo
      ctx.fillStyle = shade(s.color, 40);
      poly(ctx, [
        [w * 0.34, 0],
        [w * 0.5, h * 0.42],
        [w * 0.2, h * 0.1],
      ]);
      ctx.fill();
      poly(ctx, [
        [w * 0.66, 0],
        [w * 0.5, h * 0.42],
        [w * 0.8, h * 0.1],
      ]);
      ctx.fill();
      // cà vạt
      ctx.fillStyle = hex(s.accent);
      poly(ctx, [
        [w * 0.5, h * 0.12],
        [w * 0.56, h * 0.28],
        [w * 0.5, h * 0.5],
        [w * 0.44, h * 0.28],
      ]);
      ctx.fill();
      stroke(ctx, 1.8);
    } else if (variant === 'armor') {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, light);
      g.addColorStop(1, dark);
      ctx.fillStyle = g;
      rr(ctx, w * 0.06, 0, w * 0.88, h, 6);
      ctx.fill();
      stroke(ctx);
      ctx.fillStyle = hex(s.accent);
      // giáp vai
      ctx.beginPath();
      ctx.ellipse(w * 0.06, h * 0.1, 7, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      stroke(ctx, 2);
      ctx.beginPath();
      ctx.ellipse(w * 0.94, h * 0.1, 7, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      stroke(ctx, 2);
      // đai
      ctx.fillStyle = shade(s.accent, -30);
      rr(ctx, -1, h * 0.55, w + 2, 6, 2);
      ctx.fill();
      stroke(ctx, 2);
    } else {
      // tunic mặc định
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, light);
      g.addColorStop(1, dark);
      ctx.fillStyle = g;
      poly(ctx, [
        [w * 0.16, 0],
        [w * 0.84, 0],
        [w * 0.94, h],
        [w * 0.06, h],
      ]);
      ctx.fill();
      stroke(ctx);
      // cổ chữ V lệch sang phải (nhân vật nhìn nghiêng 3/4)
      ctx.fillStyle = hex(s.skin);
      poly(ctx, [
        [w * 0.46, 0],
        [w * 0.72, 0],
        [w * 0.58, h * 0.22],
      ]);
      ctx.fill();
      // đai lưng
      ctx.fillStyle = hex(s.accent);
      rr(ctx, -1, h * 0.6, w + 2, 5, 2);
      ctx.fill();
      stroke(ctx, 2);
    }
    void main;
  }

  // ---------------------------------------------------------------- tay chân
  private drawLimb(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const { w, h } = s;
    const g = ctx.createLinearGradient(0, 0, w, 0);
    g.addColorStop(0, shade(s.color, 22));
    g.addColorStop(1, shade(s.color, -26));
    ctx.fillStyle = g;
    rr(ctx, 0, 0, w, h, Math.min(w / 2, 5));
    ctx.fill();
    stroke(ctx, 2.2);
    // khớp
    ctx.fillStyle = shade(s.color, -14);
    ctx.beginPath();
    ctx.ellipse(w / 2, h - 2, w * 0.42, 2.4, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawFoot(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const { w, h, variant } = s;
    if (variant === 'bare') {
      ctx.fillStyle = hex(s.skin);
      rr(ctx, 0, 0, w, h * 0.9, 4);
      ctx.fill();
      stroke(ctx, 2.2);
      // ngón chân
      ctx.fillStyle = shade(s.skin, -20);
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.ellipse(w - 3 - i * 3.4, h * 0.25, 1.6, 1.9, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, shade(s.color, 20));
    g.addColorStop(1, shade(s.color, -24));
    ctx.fillStyle = g;
    if (variant === 'boots') {
      rr(ctx, -1, -2, w * 0.7, h + 2, 3);
      ctx.fill();
      stroke(ctx, 2.2);
    }
    rr(ctx, 0, h * 0.35, w, h * 0.65, 3.5);
    ctx.fill();
    stroke(ctx, 2.2);
    if (variant === 'flipflop') {
      ctx.strokeStyle = hex(s.accent);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(w * 0.42, h * 0.42);
      ctx.lineTo(w * 0.28, h * 0.82);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(w * 0.42, h * 0.42);
      ctx.lineTo(w * 0.6, h * 0.82);
      ctx.stroke();
    } else if (variant === 'cloth') {
      ctx.strokeStyle = hex(s.accent);
      ctx.lineWidth = 1.6;
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(w * 0.25 + i * 6, h * 0.5);
        ctx.lineTo(w * 0.35 + i * 6, h * 0.85);
        ctx.stroke();
      }
    }
  }

  // ---------------------------------------------------------------- tóc
  private drawHair(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const { w, h, variant } = s;
    const main = hex(s.color);
    const hi = shade(s.color, 40);
    if (variant === 'bald') {
      // chỉ 1 vệt sáng cho vui
      ctx.fillStyle = 'rgba(255,255,255,0.28)';
      ctx.beginPath();
      ctx.ellipse(w * 0.4, h * 0.3, 6, 3, -0.5, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.fillStyle = main;
    if (variant === 'bun') {
      // búi củ tỏi
      ctx.beginPath();
      ctx.arc(w / 2, h * 0.22, 7, 0, Math.PI * 2);
      ctx.fill();
      stroke(ctx, 2.2);
      ctx.beginPath();
      ctx.ellipse(w / 2, h * 0.62, w * 0.46, h * 0.34, 0, Math.PI, 0);
      ctx.fill();
      stroke(ctx, 2.2);
      ctx.fillStyle = hi;
      ctx.beginPath();
      ctx.ellipse(w * 0.36, h * 0.5, 5, 2.5, -0.4, 0, Math.PI * 2);
      ctx.fill();
    } else if (variant === 'long') {
      ctx.beginPath();
      ctx.ellipse(w / 2, h * 0.6, w * 0.58, h * 0.44, 0, Math.PI, 0);
      ctx.fill();
      stroke(ctx, 2.2);
      rr(ctx, -2, h * 0.4, 10, h * 0.6, 4);
      ctx.fill();
      stroke(ctx, 2.2);
      rr(ctx, w - 8, h * 0.4, 10, h * 0.6, 4);
      ctx.fill();
      stroke(ctx, 2.2);
    } else if (variant === 'spiky') {
      poly(ctx, [
        [0, h * 0.6], [w * 0.1, h * 0.1], [w * 0.24, h * 0.42], [w * 0.36, 0],
        [w * 0.5, h * 0.38], [w * 0.64, 0], [w * 0.76, h * 0.42], [w * 0.9, h * 0.1], [w, h * 0.6],
      ]);
      ctx.fill();
      stroke(ctx, 2.2);
    } else if (variant === 'topknot') {
      ctx.beginPath();
      ctx.ellipse(w / 2, h * 0.58, w * 0.5, h * 0.36, 0, Math.PI, 0);
      ctx.fill();
      stroke(ctx, 2.2);
      ctx.beginPath();
      ctx.arc(w / 2, h * 0.16, 6, 0, Math.PI * 2);
      ctx.fill();
      stroke(ctx, 2.2);
      ctx.fillStyle = hex(s.accent);
      rr(ctx, w / 2 - 3, h * 0.2, 6, 4, 1.5);
      ctx.fill();
    }
  }

  // ---------------------------------------------------------------- mũ
  private drawHat(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const { w, h, variant } = s;
    if (variant === 'none') return;
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, shade(s.color, 26));
    g.addColorStop(1, shade(s.color, -28));
    ctx.fillStyle = g;

    if (variant === 'bandana') {
      rr(ctx, 0, h * 0.35, w, h * 0.4, 3);
      ctx.fill();
      stroke(ctx, 2.2);
      ctx.fillStyle = hex(s.accent);
      ctx.beginPath();
      ctx.moveTo(w * 0.7, h * 0.45);
      ctx.lineTo(w * 1.05, h * 0.2);
      ctx.lineTo(w * 1.1, h * 0.75);
      ctx.closePath();
      ctx.fill();
      stroke(ctx, 2);
    } else if (variant === 'straw' || variant === 'bamboo') {
      const spread = variant === 'bamboo' ? 1.25 : 1.0;
      poly(ctx, [
        [w * 0.5, 0],
        [w * (0.5 + 0.5 * spread), h],
        [w * (0.5 - 0.5 * spread), h],
      ]);
      ctx.fill();
      stroke(ctx, 2.4);
      ctx.strokeStyle = 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 1.2;
      for (let i = 1; i <= 3; i++) {
        const t = i / 4;
        const yy = h * t;
        const half = w * 0.5 * spread * t;
        ctx.beginPath();
        ctx.moveTo(w * 0.5 - half, yy);
        ctx.lineTo(w * 0.5 + half, yy);
        ctx.stroke();
      }
      ctx.strokeStyle = OUTLINE;
    } else if (variant === 'crown') {
      poly(ctx, [
        [0, h], [0, h * 0.35], [w * 0.18, h * 0.7], [w * 0.34, h * 0.15],
        [w * 0.5, h * 0.65], [w * 0.66, h * 0.15], [w * 0.82, h * 0.7], [w, h * 0.35], [w, h],
      ]);
      ctx.fill();
      stroke(ctx, 2.4);
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(w * 0.5, h * 0.55, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // ---------------------------------------------------------------- kính
  private drawGlasses(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const { w, h, variant } = s;
    if (variant === 'none') return;
    ctx.strokeStyle = OUTLINE;
    ctx.fillStyle = variant === 'shades' ? hex(s.color) : hex(s.accent);
    ctx.lineWidth = 2.4;

    if (variant === 'eyepatch') {
      ctx.fillStyle = hex(s.color);
      ctx.beginPath();
      ctx.ellipse(w * 0.4, h * 0.5, 7, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(w * 0.4, h * 0.5 - 6);
      ctx.lineTo(w, h * 0.2);
      ctx.stroke();
      return;
    }
    const lensW = variant === 'shades' ? w * 0.4 : 6.5;
    const lensH = variant === 'shades' ? 6.5 : 6.5;
    // hai tròng lệch phải (góc 3/4): tròng gần to hơn
    for (const cx of [w * 0.4, w * 0.72]) {
      if (variant === 'round') {
        ctx.beginPath();
        ctx.arc(cx, h * 0.5, 6.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.ellipse(cx, h * 0.5, lensW / 2, lensH / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
    ctx.beginPath();
    ctx.moveTo(w * 0.4 + 6.4, h * 0.5);
    ctx.lineTo(w * 0.72 - 6.4, h * 0.5);
    ctx.stroke();
    // gọng chạy về phía sau đầu
    ctx.beginPath();
    ctx.moveTo(w * 0.4 - 6.4, h * 0.5);
    ctx.lineTo(w * 0.06, h * 0.42);
    ctx.stroke();
  }

  // ---------------------------------------------------------------- vũ khí / hào quang
  private drawWeapon(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const { w, h } = s;
    ctx.fillStyle = hex(s.color);
    rr(ctx, w / 2 - 2, 0, 4, h, 2);
    ctx.fill();
    stroke(ctx, 2);
    ctx.fillStyle = hex(s.accent);
    ctx.beginPath();
    ctx.arc(w / 2, 2, 3.4, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * NẮP KHỚP — kỹ thuật chuẩn của rig cutout.
   * Một hình tròn nhỏ đúng màu da/vải đặt ngay tại khớp xoay, vẽ ĐÈ LÊN chỗ nối.
   * Khi tay/chân xoay mạnh (đấm, đá), nắp này che khe hở ở khớp.
   */
  private drawJoint(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const r = Math.min(s.w, s.h) / 2;
    const g = ctx.createRadialGradient(r * 0.7, r * 0.7, 1, r, r, r);
    g.addColorStop(0, shade(s.color, 18));
    g.addColorStop(1, shade(s.color, -22));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(r, r, r - 1.2, 0, Math.PI * 2);
    ctx.fill();
    stroke(ctx, 2.2);
  }

  private drawAura(ctx: CanvasRenderingContext2D, s: PartStyle): void {
    const grad = ctx.createRadialGradient(s.w / 2, s.h / 2, 2, s.w / 2, s.h / 2, s.w / 2);
    grad.addColorStop(0, `rgba(255,255,255,0.55)`);
    grad.addColorStop(0.5, `${hex(s.color)}88`);
    grad.addColorStop(1, `${hex(s.color)}00`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(s.w / 2, s.h / 2, s.w / 2, 0, Math.PI * 2);
    ctx.fill();
  }
}
